<template>
  <div class="sidebar-pinned">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-file-text" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Claim Details</h4>
              <p class="mb-0 page-subtitle" v-if="claimdetails.claim_unique_id">
                {{ claimdetails.claim_unique_id }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3" :class="queueIds.length ? 'pb-queue' : 'pb-5'" id="printDiv" ref="printNow">

          <!-- Sent back to the facility for correction -->
          <div class="alert sent-back-alert mb-3" v-if="claimStatus.key === 'sent_back'">
            <div class="d-flex flex-wrap align-items-start" style="gap: 10px;">
              <i class="fe fe-corner-up-left mt-1"></i>
              <div class="flex-grow-1">
                <strong>Sent back to the facility for correction.</strong>
                <div v-if="sendBackNote" class="mt-1">{{ sendBackNote.body.replace(/^Sent back for correction:\s*/, '') }}
                  <small class="text-muted d-block">{{ sendBackNote.created_at | moment("MMM Do YYYY, h:mm a") }}</small>
                </div>
                <div v-if="isFacilityUser" class="small mt-1">Correct the flagged items (Billing, Documents), then click <b>Resubmit</b>. The claim goes back to pending.</div>
              </div>
              <button v-if="isFacilityUser" class="btn btn-sm btn-submit" @click="resubmitClaim" :disabled="reviewBusy">
                <i class="fe fe-send mr-1"></i> Resubmit
              </button>
            </div>
          </div>

          <div class="alert alert-warning mb-3" v-if="isDraftClaim">
            <i class="fe fe-edit-3 mr-1"></i> This claim is a <strong>draft</strong>. The agency can't see it
            until you click <strong>Submit for Vetting</strong>.
          </div>

          <!-- ── Action Toolbar ── -->
          <div class="card action-card shadow mb-4">
            <div class="card-body py-3 px-4">
              <div class="d-flex flex-wrap align-items-center" style="gap: 8px;">

                <!-- Primary workflow -->
                <button class="btn btn-submit btn-sm" @click="verifyClaim"
                  v-if="claimdetails.verified_by_id == null && user.job_title == 'Claims Verifier'">
                  <i class="fe fe-check mr-1"></i> Mark Verified
                </button>
                <button class="btn btn-submit btn-sm" @click="vetClaim"
                  v-if="claimdetails.checked_by_id == null && (user.job_title == 'Claims Vetter' || user.type == 'tpa_employee')">
                  <i class="fe fe-check mr-1"></i> Mark Vetted
                </button>
                <button class="btn btn-outline-warning btn-sm" @click="updateClaimStatus(-1, 'suspend')"
                  v-if="claimdetails.status != 1 && claimdetails.status != -1 && user.type != 'provider_employee' && user.type != 'provider'">
                  <i class="fe fe-pause-circle mr-1"></i> Suspend Claim
                </button>
                <button class="btn btn-outline-danger btn-sm" @click="updateClaimStatus(-2, 'reject')"
                  v-if="claimdetails.status != 1 && claimdetails.status != -2 && user.type != 'provider_employee' && user.type != 'provider'">
                  <i class="fe fe-x-circle mr-1"></i> Reject Claim
                </button>
                <button class="btn btn-outline-sent-back btn-sm" @click="openSendBack" v-if="canSendBack"
                  title="Return the claim to the facility to correct it; it comes back as pending when they resubmit">
                  <i class="fe fe-corner-up-left mr-1"></i> Send Back
                </button>

                <!-- Facility: draft claims (hidden from the agency until submitted) -->
                <button class="btn btn-submit btn-sm" @click="submitDraftClaim" :disabled="draftBusy"
                  v-if="isFacilityUser && isDraftClaim">
                  <i class="fe fe-send mr-1"></i> Submit for Vetting
                </button>
                <button class="btn btn-outline-secondary btn-sm" @click="returnClaimToDraft" :disabled="draftBusy"
                  v-if="isFacilityUser && canReturnToDraft">
                  <i class="fe fe-edit-3 mr-1"></i> Move Back to Draft
                </button>

                <!-- Tools -->
                <a target="_blank" :href="patientLink(claimdetails.enrollee)"
                  v-if="claimdetails.enrollee != null">
                  <button class="btn btn-tool btn-sm"><i class="fe fe-user mr-1"></i> Enrollee</button>
                </a>
                <button class="btn btn-tool btn-sm" @click="shownotes = !shownotes">
                  <i class="fe fe-clipboard mr-1"></i> Notes
                  <span class="badge badge-pill badge-secondary ml-1">{{ comments.length }}</span>
                </button>
                <button class="btn btn-tool btn-sm" @click="show = !show">
                  <i class="fe fe-plus mr-1"></i> Add Note
                </button>
                <router-link :to="{ path: '/upload-claims-docs/' + claimdetails.id }" v-if="claimdetails.status != 1">
                  <button class="btn btn-tool btn-sm">
                    <i class="fe fe-file-plus mr-1"></i> Documents
                    <span class="badge badge-pill badge-secondary ml-1">{{ singleclaim.docs && singleclaim.docs.length }}</span>
                  </button>
                </router-link>
                <router-link :to="{ path: '/service-processing-form/' + claimdetails.id }" v-if="claimdetails.status != 1">
                  <button class="btn btn-tool btn-sm"><i class="fe fe-thermometer mr-1"></i> Billing</button>
                </router-link>
                <router-link :to="{ path: '/encounter/' + claimdetails.servicesummary.id }"
                  v-if="claimdetails.servicesummary != null && claimdetails.status != 1">
                  <button class="btn btn-tool btn-sm"><i class="fe fe-activity mr-1"></i> Encounter</button>
                </router-link>
                <button class="btn btn-tool btn-sm" data-toggle="modal" data-target="#autoVetModal" @click="resetAutoVet">
                  <i class="fe fe-heart mr-1"></i> AI Vet
                </button>
                <button class="btn btn-tool btn-sm" @click="fetchClaimSummary" data-toggle="modal" data-target="#claimSummaryModal">
                  <i class="fe fe-bar-chart-2 mr-1"></i> Summary
                </button>
                <button class="btn btn-tool btn-sm" v-if="canDeductDrugs" @click="applyClaimDrugDeduction" :disabled="reviewBusy"
                  :title="drugDeductionLine ? 'Recalculates the existing 10% drug deduction instead of adding another one' : '10% of the drug amounts'">
                  <i class="fe fe-scissors mr-1"></i> {{ drugDeductionLine ? 'Update 10% Drug Deduction' : 'Deduct 10% Drugs' }}
                </button>
                <button class="btn btn-tool btn-sm" v-if="user.type != 'client'" @click="historyOpen = true">
                  <i class="fe fe-archive mr-1"></i> Previous Claims
                </button>
                <button class="btn btn-tool btn-sm" v-if="['shis', 'employee', 'nhia'].includes(user.type)" @click="activityOpen = true">
                  <i class="fe fe-clock mr-1"></i> Activity log
                </button>
                <button class="btn btn-tool btn-sm" @click="printMe">
                  <i class="fe fe-printer mr-1"></i> Print
                </button>
              </div>
            </div>

            <!-- Add Note textarea -->
            <div class="card-body py-3 px-4 border-top" v-show="show">
              <label class="form-label">Leave a Comment</label>
              <textarea class="form-control mb-3" rows="4" v-model="body" placeholder="Write your note here…"></textarea>
              <button type="button" class="btn btn-submit btn-sm" @click="addComment">
                <i class="fe fe-send mr-1"></i> Submit
              </button>
            </div>
          </div>

          <!-- ── Request Timeline ── -->
          <div class="card shadow table-card mb-4">
            <div class="card-body py-3">
              <div class="h-timeline">

                <!-- Authorization -->
                <div class="h-timeline-step" :class="{ 'h-timeline-step--muted': !claimdetails.auth_code }">
                  <div class="h-timeline-icon" :class="claimdetails.auth_code ? 'bg-info' : 'h-timeline-icon--muted'">
                    <i class="fe fe-key"></i>
                  </div>
                  <div class="h-timeline-label">
                    <strong>Authorization</strong>
                    <template v-if="claimdetails.auth_code">
                      <router-link :to="`/authorization-code/${claimdetails.auth_code.id}`" target="_blank">
                        {{ claimdetails.authorization_code }}
                      </router-link>
                    </template>
                    <span v-else>Not Required</span>
                  </div>
                </div>

                <div class="h-timeline-connector h-timeline-connector--done"></div>

                <!-- Submitted -->
                <div class="h-timeline-step">
                  <div class="h-timeline-icon bg-primary">
                    <i class="fe fe-file-plus"></i>
                  </div>
                  <div class="h-timeline-label">
                    <strong>Submitted</strong>
                    <span>{{ prepared_by ? prepared_by.full_name : '—' }}</span>
                    <span>{{ claimdetails.created_at | moment("MMM Do, h:mm a") }}</span>
                  </div>
                </div>

                <div class="h-timeline-connector" :class="{ 'h-timeline-connector--done': claimdetails.checked_by_id }"></div>

                <!-- Vetted -->
                <div class="h-timeline-step" :class="{ 'h-timeline-step--muted': !claimdetails.checked_by_id }">
                  <div class="h-timeline-icon" :class="claimdetails.checked_by_id ? 'h-timeline-icon--indigo' : 'h-timeline-icon--muted'">
                    <i class="fe fe-shield"></i>
                  </div>
                  <div class="h-timeline-label">
                    <strong>Vetted</strong>
                    <template v-if="claimdetails.checked_by_id">
                      <span>{{ claimdetails.checkeduser ? claimdetails.checkeduser.full_name : '—' }}</span>
                      <span>{{ claimdetails.checked_by_date }}</span>
                    </template>
                    <span v-else>Pending</span>
                  </div>
                </div>

                <div class="h-timeline-connector" :class="{ 'h-timeline-connector--done': claimdetails.verified_by_id }"></div>

                <!-- Verified -->
                <div class="h-timeline-step" :class="{ 'h-timeline-step--muted': !claimdetails.verified_by_id }">
                  <div class="h-timeline-icon" :class="claimdetails.verified_by_id ? 'bg-info' : 'h-timeline-icon--muted'">
                    <i class="fe fe-check-square"></i>
                  </div>
                  <div class="h-timeline-label">
                    <strong>Verified</strong>
                    <template v-if="claimdetails.verified_by_id">
                      <span>{{ claimdetails.verfieduser ? claimdetails.verfieduser.full_name : '—' }}</span>
                      <span>{{ claimdetails.verified_by_date }}</span>
                    </template>
                    <span v-else>Pending</span>
                  </div>
                </div>

                <div class="h-timeline-connector"
                  :class="{ 'h-timeline-connector--done': claimdetails.approved_by_id || claimStatus.key === 'rejected' || claimStatus.key === 'suspended' }"></div>

                <!-- Approved -->
                <div class="h-timeline-step"
                  :class="{ 'h-timeline-step--muted': !claimdetails.approved_by_id && claimStatus.key !== 'rejected' && claimStatus.key !== 'suspended' }">
                  <div class="h-timeline-icon" :class="
                      claimStatus.key === 'rejected' ? 'bg-danger'
                        : claimStatus.key === 'suspended' ? 'bg-dark'
                        : claimdetails.approved_by_id ? 'bg-success'
                        : 'h-timeline-icon--muted'
                    ">
                    <i :class="['fe', claimStatus.key === 'rejected' ? 'fe-x-circle' : claimStatus.key === 'suspended' ? 'fe-pause-circle' : 'fe-check-circle']"></i>
                  </div>
                  <div class="h-timeline-label">
                    <strong>
                      {{ claimStatus.key === 'rejected' ? 'Rejected' : claimStatus.key === 'suspended' ? 'Suspended' : 'Approved' }}
                    </strong>
                    <template v-if="claimdetails.approved_by_id">
                      <span>{{ claimdetails.approveduser ? claimdetails.approveduser.full_name : '—' }}</span>
                      <span>{{ claimdetails.approved_by_date }}</span>
                    </template>
                    <span v-else-if="claimStatus.key !== 'rejected' && claimStatus.key !== 'suspended'">Pending</span>
                  </div>
                </div>

                <div class="h-timeline-connector" :class="{ 'h-timeline-connector--done': claimdetails.paymentorders && claimdetails.paymentorders.length }"></div>

                <!-- Paid -->
                <div class="h-timeline-step" :class="{ 'h-timeline-step--muted': !(claimdetails.paymentorders && claimdetails.paymentorders.length) }">
                  <div class="h-timeline-icon"
                    :class="claimdetails.paymentorders && claimdetails.paymentorders.length ? (claimStatus.key === 'paid' ? 'bg-success' : 'h-timeline-icon--indigo') : 'h-timeline-icon--muted'">
                    <i class="fe fe-credit-card"></i>
                  </div>
                  <div class="h-timeline-label">
                    <strong>{{ claimStatus.key === 'paid' ? 'Paid' : 'Payment' }}</strong>
                    <template v-if="claimdetails.paymentorders && claimdetails.paymentorders.length">
                      <router-link :to="`/claims/transaction/${claimdetails.paymentorders[0].id}`" target="_blank">
                        {{ claimdetails.paymentorders[0].payment_number }}
                      </router-link>
                    </template>
                    <span v-else>Not yet</span>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <!-- ── Comments (collapsible) ── -->
          <div v-show="shownotes" class="mb-4">
            <div v-if="comments && comments.length">
              <div v-for="comment in comments" :key="comment.id" class="media mb-3 p-3"
                style="background: #f8f9fa; border-left: 3px solid #5b8dee; border-radius: 4px;">
                <div class="avatar avatar-sm mr-3">
                  <div class="avatar-title rounded-circle bg-soft-info text-info" style="font-size: 0.75rem;">
                    <i class="fe fe-user"></i>
                  </div>
                </div>
                <div class="media-body">
                  <p class="mb-1">{{ comment.body }}</p>
                  <small class="text-muted">
                    <strong>{{ comment.user.full_name }}</strong>
                    &middot; {{ comment.user.type }}
                    &middot; {{ comment.created_at | moment("MMM Do YYYY, h:mm a") }}
                  </small>
                </div>
              </div>
            </div>
            <p v-else class="text-muted text-center py-3">No comments yet.</p>
          </div>

          <!-- ── Claim Info Cards ── -->
          <div class="row mb-4">
            <!-- Status badges -->
            <div class="col-12 mb-3">

              <div class="d-flex flex-wrap" style="gap: 6px;">
                <span class="badge px-3 py-2" :class="'badge-soft-' + claimStatus.variant" style="font-size: 0.8rem;">
                  <i :class="['fe', 'mr-1', claimStatus.icon]"></i> {{ claimStatus.label }}
                </span>
              </div>
            </div>

            <!-- Enrollee & Claim info -->
            <div class="col-md-6 mb-4">
              <div class="card info-card h-100">
                <div class="info-card-header">
                  <i class="fe fe-user mr-2"></i> Enrollee Information
                </div>
                <div v-if="claimdetails.enrollee != null" class="text-center py-3"
                  style="background: #f7f9ff; border-bottom: 1px solid #e9ecef;">
                  <a v-if="claimdetails.enrollee.user_image" target="_blank" :href="patientLink(claimdetails.enrollee)"
                    title="Open patient page">
                    <img :src="claimdetails.enrollee.image_url" class="rounded-circle text-center mx-auto d-block"
                      style="width: 80px; height: 80px; object-fit: cover; border: 3px solid #2563eb;"
                      onerror="this.onerror=null; this.src='/assets/img/claimify_logo.png'" />
                  </a>
                  <div v-else class="rounded-circle mx-auto d-flex align-items-center justify-content-center"
                    style="width: 80px; height: 80px; background: #eef2ff; border: 3px solid #c7d2e8;">
                    <i class="fe fe-user" style="font-size: 2rem; color: #2563eb;"></i>
                  </div>
                  <p class="mb-0 font-weight-bold mt-2" style="font-size: 0.9rem;">
                    {{ claimdetails.enrollee.full_name }}
                  </p>
                  <small class="text-muted">{{ claimdetails.enrollee.id_card_number }}</small>
                </div>
                <div class="list-group list-group-flush">
                  <div class="list-group-item px-3 py-2 d-flex align-items-start" v-if="claimdetails.enrollee != null">
                    <i class="fe fe-user text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Name</p>
                      <a target="_blank" :href="patientLink(claimdetails.enrollee)">
                        <p class="mb-0 font-weight-bold">
                          {{ claimdetails.enrollee.full_name }} ({{ claimdetails.enrollee.id_card_number }})
                        </p>

                        <p>{{ claimdetails.enrollee.sector }}</p>
                      </a>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start" v-if="claimdetails.enrollee != null">
                    <i class="fe fe-calendar text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Date of Birth</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.enrollee.dob }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start" v-if="claimdetails.enrollee != null">
                    <i class="fe fe-phone text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Phone Number</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.enrollee.phone_number }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-home text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Health Facility</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.provider.agency_name }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-navigation text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Out of Station</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.is_out_of_station ? 'Yes' : 'No' }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start" v-if="claimdetails.auth_code != null">
                    <i class="fe fe-key text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Authorization Code
                      </p>
                      <a :href="`/authorization-code/${claimdetails.auth_code.id}`" target="_blank"
                        class="font-weight-bold">
                        {{ claimdetails.authorization_code }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Claim details -->
            <div class="col-md-6 mb-4">
              <div class="card info-card h-100">
                <div class="info-card-header">
                  <i class="fe fe-file-text mr-2"></i> Claim Details
                </div>
                <div class="list-group list-group-flush">
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-alert-circle text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Diagnosis</p>
                      <p class="mb-0 font-weight-bold">
                        <span v-if="claimdetails.diagnosis.diagnosis_code" class="text-muted font-weight-normal mr-1">({{ claimdetails.diagnosis.diagnosis_code }})</span>{{ claimdetails.diagnosis.name }}
                      </p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-message-square text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Reason for Claim</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.treatment }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-layers text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Treatment Level</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.claim_level }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-user-check text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Prepared By</p>
                      <p class="mb-0 font-weight-bold">{{ prepared_by.full_name }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-clock text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Date Created</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.created_at | moment("ddd, MMM Do YYYY") }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start"
                    v-if="claimdetails.checked_by_id != null">
                    <i class="fe fe-shield text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Vetted By · Date</p>
                      <p class="mb-0 font-weight-bold">
                        {{ claimdetails.checkeduser.full_name }}
                        <span class="text-muted font-weight-normal">· {{ claimdetails.checked_by_date }}</span>
                      </p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start"
                    v-if="claimdetails.verified_by_id != null">
                    <i class="fe fe-check-square text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Verified By · Date
                      </p>
                      <p class="mb-0 font-weight-bold">
                        {{ claimdetails.verfieduser.full_name }}
                        <span class="text-muted font-weight-normal">· {{ claimdetails.verified_by_date }}</span>
                      </p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start"
                    v-if="claimdetails.approved_by_id != null">
                    <i class="fe fe-check-circle text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Approved By · Date
                      </p>
                      <p class="mb-0 font-weight-bold">
                        {{ claimdetails.approveduser.full_name }}
                        <span class="text-muted font-weight-normal">· {{ claimdetails.approved_by_date }}</span>
                      </p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start"
                    v-if="claimdetails.paymentorders.length >= 1">
                    <i class="fe fe-credit-card text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Payment Order</p>
                      <a :href="`/transaction/${claimdetails.paymentorders[0].id}`" class="font-weight-bold text-info">
                        {{ claimdetails.paymentorders[0].payment_number }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Fraud signals (agency staff) ── -->
          <FraudSignals :claim-id="$route.params.id" />

          <!-- ── Services / Drugs Table ── -->
          <div class="card info-card mb-4">
            <div class="info-card-header d-flex align-items-center justify-content-between">
              <span><i class="fe fe-thermometer mr-2"></i> Services / Drugs</span>
              <button
                v-if="(user.type == 'shis' || user.type == 'employee') && claimdetails.status != 1"
                class="btn btn-sm btn-accept-all"
                @click="acceptAllVetAmounts"
              >
                <i class="fe fe-check-circle mr-1"></i> Accept All Vet Amounts
              </button>
            </div>
            <div class="card-body p-0">
              <!-- Bulk actions on the ticked items -->
              <div class="bulk-bar" v-if="canReviewLines && selectedLines.length">
                <span class="font-weight-bold">{{ selectedLines.length }} item(s) selected</span>
                <select class="form-control form-control-sm" style="width: auto; min-width: 200px;" v-model="bulkRemark">
                  <option value="">Rejection reason…</option>
                  <option :value="remark.id" v-for="remark in remarks" :key="remark.id">{{ remark.name }}</option>
                </select>
                <input type="text" class="form-control form-control-sm" style="max-width: 260px;" v-model="bulkComment"
                  placeholder="Comment (optional)" />
                <button class="btn btn-sm btn-danger" @click="rejectSelectedLines" :disabled="reviewBusy">
                  <i class="fe fe-x-circle mr-1"></i> Reject selected
                </button>
                <button class="btn btn-sm btn-outline-sent-back" v-if="canSendBack" @click="openSendBack">
                  <i class="fe fe-corner-up-left mr-1"></i> Send back with these
                </button>
                <button class="btn btn-sm btn-link" @click="selectedLines = []">Clear</button>
              </div>
              <div class="table-responsive">
                <table class="table align-td-middle table-card">
                  <thead>
                    <tr>
                      <th v-if="canReviewLines" style="width: 36px;">
                        <input type="checkbox" :checked="allLinesSelected" @change="toggleAllLines($event.target.checked)"
                          aria-label="Select all items" />
                      </th>
                      <th>#</th>
                      <th>Name</th>
                      <th>Requested Cost</th>
                      <th>Vetted Amount</th>
                      <th>Verified Amount</th>
                      <th>Verdict</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(service, index) in singleclaim.services" :key="service.id"
                      :class="{ 'row-selected': selectedLines.includes(service.id) }">
                      <td v-if="canReviewLines">
                        <input type="checkbox" :value="service.id" v-model="selectedLines" :aria-label="'Select item ' + (index + 1)" />
                      </td>
                      <td>{{ index + 1 }}</td>
                      <td>
                        <span v-if="service.service != null">{{ service.service.description }}</span>
                        <span v-if="service.drugs_id != null">{{ service.drug.drug_name }}</span>
                      </td>
                      <td>&#8358;{{ service.cost | numeral(0, 0) }}</td>
                      <td>
                        <span v-show="listprice">&#8358;{{ service.vetted_price | numeral(0, 0) }}</span>
                        <div class="btn-group CTAs" v-if="service.ai != null">
                          <button type="button" class="btn btn-info badge dropdown-toggle" data-toggle="dropdown"
                            aria-haspopup="true" aria-expanded="false">
                            ₦{{ service.ai.vetted_price | numeral(0, 0) }}
                          </button>
                          <div class="dropdown-menu">
                            <button class="btn btn-success btn-block badge" @click="updateServicePrice(service)">
                              Accept <i class="fe fe-check"></i>
                            </button>
                            <button class="btn btn-danger btn-block badge" @click="updateprice_vet = true">
                              Reject <i class="fe fe-x"></i>
                            </button>
                          </div>
                        </div>
                        <input type="text" class="form-control m-2" v-show="updateprice_vet"
                          @change="updateServicePrice(service)" v-model="service.vetted_price" />
                      </td>
                      <td>
                        <span v-show="listprice">&#8358;{{ service.verified_price | numeral(0, 0) }}</span>
                        <input type="text" class="form-control" :id="`price${service.id}`" v-show="updateprice_ver"
                          @change="updateServicePrice(service)" v-model="service.verified_price" />
                      </td>
                      <td>
                        <span v-if="service.remark != null">{{ service.remark.name }}</span>
                      </td>
                      <td>
                        <button data-toggle="modal" data-target="#slideRightModalAll"
                          class="btn btn-sm btn-outline-info mr-1" @click="getSingleService(service)">
                          <i class="fe fe-eye"></i>
                        </button>
                        <div class="d-inline-block" v-if="claimdetails.status != 1">
                          <div v-if="service.id == rejectingServiceId" class="mt-2" style="min-width: 200px;">
                            <label class="small font-weight-bold text-danger mb-1 d-block">Rejection Remark
                              <span class="text-danger">*</span></label>
                            <select class="form-control form-control-sm mb-2" v-model="remark_selected">
                              <option value="">-- Select reason --</option>
                              <option :value="remark.id" v-for="remark in remarks" :key="remark.id">{{ remark.name }}
                              </option>
                            </select>
                            <div class="d-flex" style="gap: 4px;">
                              <button class="btn btn-sm btn-danger" @click="confirmRejectService(service)">
                                <i class="fe fe-x-circle mr-1"></i> Confirm Reject
                              </button>
                              <button class="btn btn-sm btn-outline-secondary" @click="cancelRejectService">
                                Cancel
                              </button>
                            </div>
                          </div>
                          <div v-else-if="service.id == selected_service" class="form-group mt-2 mb-0"
                            style="min-width: 160px;">
                            <select class="form-control form-control-sm" v-model="remark_selected"
                              @change="updateService(service)">
                              <option :value="remark.id" v-for="remark in remarks" :key="remark.id">{{ remark.name }}
                              </option>
                            </select>
                          </div>
                          <div v-else-if="user.type != 'provider_employee' && user.type != 'provider'"
                            class="btn-group">
                            <button type="button" class="btn btn-sm btn-outline-secondary dropdown-toggle"
                              data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                              <i class="fe fe-more-vertical"></i>
                            </button>
                            <div class="dropdown-menu dropdown-menu-right">
                              <a class="dropdown-item" role="button" @click="remarkService(service)">
                                <i class="fe fe-tag mr-1"></i> Make Verdict
                              </a>
                              <a class="dropdown-item text-danger" role="button" @click="startRejectService(service)"
                                v-if="user.job_title == 'Claims Vetter' || user.type == 'tpa_employee' || user.type == 'shis' || user.type == 'employee'">
                                <i class="fe fe-x-circle mr-1"></i> Reject Service
                              </a>
                              <a class="dropdown-item" role="button" @click="readycommentService(service)">
                                <i class="fe fe-message-square mr-1"></i> Add Comment
                              </a>
                              <div class="dropdown-divider"></div>
                              <template v-if="user.job_title == 'Claims Vetter' || user.type == 'tpa_employee'">
                                <a class="dropdown-item" role="button" @click="updateprice_vet = true">
                                  <i class="fe fe-edit-2 mr-1"></i> Vet Amount
                                </a>
                                <a class="dropdown-item text-success" role="button"
                                  @click="acceptServiceCost(service, 'vet')">
                                  <i class="fe fe-check mr-1"></i> Accept Requested Cost
                                </a>
                              </template>
                              <template v-if="user.type == 'shis' || user.type == 'employee'">
                                <div class="dropdown-divider"></div>
                                <a class="dropdown-item" role="button" @click="updateprice_ver = true">
                                  <i class="fe fe-edit-2 mr-1"></i> Verify Amount
                                </a>
                                <a class="dropdown-item text-success" role="button"
                                  @click="acceptServiceCost(service, 'verify')">
                                  <i class="fe fe-check mr-1"></i> Accept Requested Cost
                                </a>
                                <a class="dropdown-item text-primary font-weight-bold" role="button"
                                  @click="acceptVetAmount(service)">
                                  <i class="fe fe-check-circle mr-1"></i> Accept Vet Amount
                                </a>
                              </template>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr class="bg-light font-weight-bold">
                      <td :colspan="canReviewLines ? 3 : 2">Total</td>
                      <td>&#8358;{{ singleclaim.sum | numeral(0, 0) }}</td>
                      <td>&#8358;{{ singleclaim.vetted_sum | numeral(0, 0) }}</td>
                      <td>&#8358;{{ singleclaim.verified_sum | numeral(0, 0) }}</td>
                      <td colspan="2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- ── Documents Table ── -->
          <div class="card info-card mb-4">
            <div class="info-card-header">
              <i class="fe fe-file mr-2"></i> Claim Documents
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table align-td-middle table-card">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Uploaded By</th>
                      <th>Date Uploaded</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(doc, index) in singleclaim.docs" :key="doc.id">
                      <td>{{ index + 1 }}</td>
                      <td>{{ doc.name }}</td>
                      <td>{{ doc.user.full_name }}</td>
                      <td>{{ doc.created_at | moment("MMM Do YYYY") }}</td>
                      <td>
                        <a :href="'https://claimify-api.hayokmedicare.ng/documents/' + doc.document" target="_blank">
                          <button class="btn btn-sm btn-outline-info">
                            <i class="fe fe-eye"></i>
                          </button>
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

      </div><!-- end container -->

      <!-- ── Sticky Queue Navigation Bar ── -->
      <div class="queue-nav-bar" v-if="queueIds.length">
        <button class="btn btn-queue-nav" :disabled="!prevId" @click="goToQueue(prevId)">
          <i class="fe fe-chevron-left mr-1"></i> Prev
        </button>

        <div class="queue-nav-center">
          <span class="queue-nav-count">{{ queuePosition + 1 }} <span class="text-muted">of</span> {{ queueIds.length
            }}</span>
          <span class="queue-nav-label">Claims</span>
        </div>

        <button class="btn btn-queue-nav" :disabled="!nextId" @click="goToQueue(nextId)">
          Next <i class="fe fe-chevron-right ml-1"></i>
        </button>
      </div>

      <!-- ── Service Detail Modal ── -->
        <div class="modal fade modal-slide-right" id="slideRightModalAll" tabindex="-1" role="dialog"
          aria-labelledby="slideRightModalLabel" aria-hidden="true">
          <div class="modal-dialog scrollmodal" role="document">
            <div class="modal-content">
              <div class="modal-header bg-soft-info" style="border-bottom: 2px solid #e3eaf0;">
                <h5 class="modal-title" id="slideRightModalLabel">
                  <span v-if="singlservicemodal.service != null">{{ singlservicemodal.service.description }}</span>
                  <span v-if="singlservicemodal.drug != null">{{ singlservicemodal.drug.drug_name }}: {{
                    singlservicemodal.drug.item_data }}</span>
                </h5>
                <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="modal-body">
                <div class="list-group list-group-flush mb-3">
                  <div class="list-group-item px-0 py-2">
                    <small class="text-muted text-uppercase">Unit Amount</small>
                    <p class="mb-0 font-weight-bold">
                      &#8358;
                      <span v-if="singlservicemodal.service != null">{{ singlservicemodal.service.price }}</span>
                      <span v-if="singlservicemodal.drugs_id != null">{{ singlservicemodal.drug.price }}</span>
                    </p>
                  </div>
                  <div class="list-group-item px-0 py-2">
                    <small class="text-muted text-uppercase"> <span v-if="singlservicemodal.drugs_id"> Dose / </span>
                      Frequency / Days</small>
                    <p class="mb-0 font-weight-bold">
                    <span v-if="singlservicemodal.drugs_id"> {{ singlservicemodal.dose }} · </span>   {{ singlservicemodal.frequency }}x/day · {{ singlservicemodal.days
                      }} days
                    </p>
                  </div>
                  <div class="list-group-item px-0 py-2">
                    <small class="text-muted text-uppercase">Verdict</small>
                    <p class="mb-0 font-weight-bold">
                      <span v-if="singlservicemodal.remark != null">{{ singlservicemodal.remark.name }}</span>
                      <span v-else class="text-muted">—</span>
                    </p>
                  </div>
                  <div class="list-group-item px-0 py-2">
                    <small class="text-muted text-uppercase">Requested Amount</small>
                    <p class="mb-0 font-weight-bold">&#8358;{{ singlservicemodal.vetted_price }}</p>
                  </div>
                  <div class="list-group-item px-0 py-2">
                    <small class="text-muted text-uppercase">Approved Amount</small>
                    <p class="mb-0 font-weight-bold">&#8358;{{ singlservicemodal.verified_price }}</p>
                  </div>
                </div>

                <!-- How often this patient has had this service / drug -->

                <ServiceUsage v-if="claimdetails && claimdetails.id" :claim-id="claimdetails.id" :line-id="singlservicemodal && singlservicemodal.id" />


                <!-- Verdict -->
                <div class="card mb-3" style="border-left: 4px solid #5b8dee;"
                  v-if="claimdetails.status != 1 && user.type != 'provider_employee' && user.type != 'provider'">
                  <div class="card-body py-3">
                    <p class="mb-2 font-weight-bold" style="font-size: 0.85rem;">Verdict</p>
                    <select class="form-control form-control-sm mb-2" v-model="modalVerdict">
                      <option value="">-- Select verdict --</option>
                      <option :value="remark.id" v-for="remark in remarks" :key="remark.id">{{ remark.name }}</option>
                    </select>
                    <button class="btn btn-info btn-block btn-sm" @click="saveModalVerdict">
                      <i class="fe fe-tag mr-1"></i> Save Verdict
                    </button>
                  </div>
                </div>

                <!-- Price Calculator -->
                <div class="card mb-3" style="border-left: 4px solid #5b8dee;"
                  v-if="claimdetails.status != 1 && user.type != 'provider_employee' && user.type != 'provider'">
                  <div class="card-body py-3">
                    <p class="mb-2 font-weight-bold" style="font-size: 0.85rem;">Price Calculator</p>
                    <div class="calc-rows mb-2">
                      <div class="calc-row" v-if="singlservicemodal.drugs_id != null">
                        <label class="calc-row-label" for="calcDoseInput">Dose</label>
                        <input id="calcDoseInput" type="number" class="form-control form-control-sm" v-model="calc.dose" />
                      </div>
                      <div class="calc-row">
                        <label class="calc-row-label" for="calcFrequencyInput">Frequency</label>
                        <input id="calcFrequencyInput" type="number" class="form-control form-control-sm" v-model="calc.frequency" />
                      </div>
                      <div class="calc-row">
                        <label class="calc-row-label" for="calcDaysInput">Days</label>
                        <input id="calcDaysInput" type="number" class="form-control form-control-sm" v-model="calc.days" />
                      </div>
                      <div class="calc-row bg-light">
                        <strong>Total</strong>
                        <strong>&#8358;{{ calculateTotal | numeral(0, 0) }}</strong>
                      </div>
                    </div>
                    <div class="d-flex" style="gap: 6px;">
                      <button class="btn btn-outline-info btn-block btn-sm"
                        v-if="user.job_title == 'Claims Vetter' || user.type == 'tpa_employee'"
                        @click="applyCalcToPrice('vetted_price')">
                        <i class="fe fe-shield mr-1"></i> Apply to Vetted
                      </button>
                      <button class="btn btn-outline-info btn-block btn-sm"
                        v-if="user.type == 'shis' || user.type == 'employee'"
                        @click="applyCalcToPrice('verified_price')">
                        <i class="fe fe-check-square mr-1"></i> Apply to Verified
                      </button>
                    </div>

                    <hr class="my-3" />

                    <div class="custom-control custom-switch">
                      <input type="checkbox" class="custom-control-input" id="manualPriceSwitch"
                        v-model="showManualPrice" />
                      <label class="custom-control-label font-weight-bold" for="manualPriceSwitch"
                        style="font-size: 0.85rem;">
                        Set Amount Manually
                      </label>
                    </div>
                    <div class="mt-2" v-show="showManualPrice">
                      <div class="form-group mb-2" v-if="user.job_title == 'Claims Vetter' || user.type == 'tpa_employee'">
                        <label class="small text-muted mb-1">Vetted Amount</label>
                        <div class="input-group input-group-sm">
                          <input type="number" class="form-control" v-model="singlservicemodal.vetted_price" />
                          <div class="input-group-append">
                            <button class="btn btn-info" @click="updateServicePrice(singlservicemodal)">Save</button>
                          </div>
                        </div>
                      </div>
                      <div class="form-group mb-0" v-if="user.type == 'shis' || user.type == 'employee'">
                        <label class="small text-muted mb-1">Verified Amount</label>
                        <div class="input-group input-group-sm">
                          <input type="number" class="form-control" v-model="singlservicemodal.verified_price" />
                          <div class="input-group-append">
                            <button class="btn btn-info" @click="updateServicePrice(singlservicemodal)">Save</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Service Comments -->
                <p class="font-weight-bold mb-2">Comments</p>
                <div v-for="comment in singlservicemodal.servicecomments" :key="comment.id" class="media mb-2 p-2"
                  style="background: #f8f9fa; border-radius: 4px;">
                  <div class="media-body">
                    <p class="mb-1" style="font-size: 0.85rem;">{{ comment.body }}</p>
                    <small class="text-muted">
                      <strong>{{ comment.user.full_name }}</strong> · {{ comment.created_at | moment("from", "now") }}
                    </small>
                  </div>
                </div>
                <div v-if="!singlservicemodal.servicecomments || !singlservicemodal.servicecomments.length"
                  class="text-muted text-center py-2" style="font-size: 0.85rem;">
                  No comments yet.
                </div>

                <!-- Add Comment -->
                <div class="mt-2" v-if="claimdetails.status != 1 && user.type != 'provider_employee' && user.type != 'provider'">
                  <textarea class="form-control form-control-sm mb-2" rows="3" v-model="modalCommentBody"
                    placeholder="Write a comment on this item…"></textarea>
                  <button class="btn btn-info btn-block btn-sm" @click="addModalComment">
                    <i class="fe fe-send mr-1"></i> Add Comment
                  </button>
                </div>
              </div>
              <div class="modal-footer" style="border-top: 2px solid #e3eaf0;">
                <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
      <ActivityLogPanel v-if="claimdetails && claimdetails.id" v-model="activityOpen" type="claim" :record-id="claimdetails.id"
        :subtitle="'Claim ' + (claimdetails.claim_unique_id || '#' + claimdetails.id) + ': who did what and when'" />
      <ClaimHistoryPanel v-if="claimdetails && claimdetails.id" v-model="historyOpen" :claim-id="claimdetails.id"
        :subtitle="(claimdetails.enrollee ? claimdetails.enrollee.full_name + ' · ' + (claimdetails.enrollee.id_card_number || '') : 'This patient') + ': earlier claims and their items'" />

      <!-- Send back to the facility -->
      <div v-if="sendBackOpen" class="sb-backdrop" @click.self="sendBackOpen = false">
        <div class="sb-dialog" role="dialog" aria-modal="true" aria-labelledby="sb-title">
          <div class="sb-head">
            <h6 id="sb-title" class="mb-0 font-weight-bold text-white"><i class="fe fe-corner-up-left mr-2"></i> Send claim back to the facility</h6>
            <button type="button" class="close text-white" aria-label="Close" @click="sendBackOpen = false"><span aria-hidden="true">&times;</span></button>
          </div>
          <div class="p-3">
            <p class="small text-muted mb-2">
              The facility is told in chat and sees your note on the claim. It corrects the claim and resubmits it, and the
              claim comes back as <b>pending</b> for vetting. Vetting and verification marks are cleared.
            </p>
            <label class="form-label">What needs to be corrected? <span class="text-danger">*</span></label>
            <textarea class="form-control mb-2" rows="4" v-model="sendBackReason"
              placeholder="e.g. Attach the lab result for the malaria test and correct the ACT quantity"></textarea>
            <div v-if="selectedLines.length" class="small mb-2">
              <i class="fe fe-tag mr-1"></i> The note is also added to the {{ selectedLines.length }} ticked item(s):
              <span class="text-muted">{{ selectedLineNames.join(", ") }}</span>
            </div>
            <div v-else class="small text-muted mb-2">Tip: tick items in the table first to flag them for the facility.</div>
            <div class="d-flex justify-content-end" style="gap: 8px;">
              <button class="btn btn-outline-secondary btn-sm" @click="sendBackOpen = false">Cancel</button>
              <button class="btn btn-sent-back btn-sm" @click="sendBackClaim" :disabled="reviewBusy || sendBackReason.trim().length < 3">
                <i class="fe fe-send mr-1"></i> Send back
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
    <Footer />

    <!-- ── AI Auto Vetting Modal ── -->
    <div class="modal fade modal-slide-right" id="autoVetModal" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog scrollmodal" role="document" style="width: 600px; max-width: 100%;">
        <div class="modal-content">
          <div class="modal-header bg-soft-info" style="border-bottom: 2px solid #e3eaf0;">
            <div class="d-flex align-items-center">
              <div class="avatar avatar-sm mr-3">
                <div class="avatar-title rounded-circle bg-info text-white"><i class="fe fe-heart"></i></div>
              </div>
              <h5 class="modal-title mb-0">AI Auto Vetting</h5>
            </div>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <!-- Layer Selection -->
            <div class="card mb-3" style="border-left: 4px solid #5b8dee;">
              <div class="card-body py-3">
                <p class="mb-2 font-weight-bold" style="font-size: 0.85rem;">
                  <i class="fe fe-sliders mr-1 text-info"></i> Select Vetting Layers
                </p>
                <div class="custom-control custom-checkbox mb-2">
                  <input type="checkbox" class="custom-control-input" id="layerA" value="A" v-model="vetLayers" />
                  <label class="custom-control-label" for="layerA">
                    <strong>A — Historical Benchmarking</strong>
                    <span class="text-muted d-block" style="font-size: 0.78rem;">Compares submitted price against past
                      verified prices for same diagnosis, age group and gender.</span>
                  </label>
                </div>
                <div class="custom-control custom-checkbox mb-3" v-if="canUseHiva">
                  <input type="checkbox" class="custom-control-input" id="layerC" value="C" v-model="vetLayers" />
                  <label class="custom-control-label" for="layerC">
                    <strong>HIVA Trace AI Clinical Review</strong>
                    <span class="text-muted d-block" style="font-size: 0.78rem;">Sends the claim, its drugs and services
                      and the enrollee's coverage (no names or phone numbers) to HIVA TRACE for price, dosage,
                      eligibility and drug checks.</span>
                  </label>
                </div>

                <!-- HIVA TRACE options -->
                <div v-if="canUseHiva && vetLayers.includes('C')" class="hiva-options mb-3">
                  <p class="mb-1 text-muted" style="font-size: 0.72rem; text-transform: uppercase;">HIVA TRACE checks</p>
                  <div class="d-flex flex-wrap mb-2" style="gap: 12px;">
                    <div class="custom-control custom-checkbox" v-for="opt in hivaCheckOptions" :key="opt.value">
                      <input type="checkbox" class="custom-control-input" :id="'hivaCheck-' + opt.value" :value="opt.value"
                        v-model="hivaChecks" />
                      <label class="custom-control-label" :for="'hivaCheck-' + opt.value" style="font-size: 0.8rem;">
                        {{ opt.label }}</label>
                    </div>
                  </div>
                  <div class="custom-control custom-switch mb-1">
                    <input type="checkbox" class="custom-control-input" id="hivaIncludeAi" v-model="hivaIncludeAi" />
                    <label class="custom-control-label" for="hivaIncludeAi" style="font-size: 0.8rem;">
                      Include AI recommendation <span class="text-muted">(slower)</span></label>
                  </div>
                  <div class="custom-control custom-switch">
                    <input type="checkbox" class="custom-control-input" id="hivaForce" v-model="hivaForce" />
                    <label class="custom-control-label" for="hivaForce" style="font-size: 0.8rem;">
                      Run again even if vetted in the last hour</label>
                  </div>
                  <small class="text-muted d-block mt-1" style="font-size: 0.72rem;">
                    <i class="fe fe-info mr-1"></i>Each HIVA TRACE run uses agency credit. An unchanged claim vetted in the
                    last hour shows the saved result instead.
                  </small>
                </div>
                <button class="btn btn-info btn-block" @click="runAutoVet" :disabled="vetLoading || !vetLayers.length">
                  <span v-if="vetLoading"><span class="spinner-border spinner-border-sm mr-1"></span> Analysing…</span>
                  <span v-else><i class="fe fe-play mr-1"></i> Run Analysis</span>
                </button>
              </div>
            </div>

            <!-- HIVA TRACE: previous results for this claim -->
            <div class="card mb-3" v-if="canUseHiva && (hivaHistoryLoading || hivaHistory.length)">
              <div class="card-header py-2 d-flex align-items-center justify-content-between">
                <span class="font-weight-bold" style="font-size: 0.82rem;">
                  <i class="fe fe-clock text-info mr-1"></i> Previous HIVA TRACE results
                </span>
                <span v-if="hivaHistoryLoading" class="spinner-border spinner-border-sm text-muted"></span>
              </div>
              <ul class="list-group list-group-flush">
                <li v-for="v in hivaHistory.slice(0, 5)" :key="v.id"
                  class="list-group-item py-2 d-flex align-items-center justify-content-between"
                  :class="{ 'bg-soft-info': hivaView && hivaView.id === v.id }" style="font-size: 0.78rem;">
                  <div>
                    <span v-if="v.error" class="badge badge-soft-danger mr-1">Error</span>
                    <span v-else :class="hivaBadge(v.verdict)" class="mr-1">{{ v.verdict || '—' }}</span>
                    {{ v.created_at }}
                    <span class="text-muted" v-if="v.requester"> · {{ v.requester.full_name }}</span>
                    <span class="text-muted" v-if="!v.error && !v.ai_included"> · no AI</span>
                    <span v-if="v.error" class="d-block text-danger" style="font-size: 0.72rem;">{{ v.error }}</span>
                  </div>
                  <button v-if="!v.error && v.response" class="btn btn-link btn-sm p-0" @click="viewHivaResult(v)">
                    {{ hivaView && hivaView.id === v.id ? 'Hide' : 'View' }}
                  </button>
                </li>
              </ul>
            </div>

            <!-- HIVA TRACE error (the other layers' results still show) -->
            <div v-if="vetResults && vetResults.external_api_error" class="alert alert-danger mb-3" style="font-size: 0.82rem;">
              <i class="fe fe-alert-triangle mr-1"></i>
              <strong>HIVA TRACE:</strong> {{ vetResults.external_api_error.message }}
            </div>

            <!-- Results -->
            <div v-if="vetResults || hivaReport">
              <div class="row mb-3" v-if="vetResults">
                <div class="col-4">
                  <div class="card text-center py-2">
                    <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Auto-Approved</p>
                    <h4 class="mb-0 text-success">{{ vetResults.summary.items_auto_approved }}</h4>
                  </div>
                </div>
                <div class="col-4">
                  <div class="card text-center py-2">
                    <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Flagged</p>
                    <h4 class="mb-0 text-danger">{{ vetResults.summary.items_flagged }}</h4>
                  </div>
                </div>
                <div class="col-4">
                  <div class="card text-center py-2">
                    <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Savings</p>
                    <h4 class="mb-0 text-info">&#8358;{{ vetResults.summary.savings | numeral(0, 0) }}</h4>
                  </div>
                </div>
              </div>

              <!-- AI Recommendation (Layer C) -->
              <div v-if="hivaReport && hivaReport.ai_recommendation" class="card mb-3"
                style="border-left: 4px solid #f6c23e;">
                <div class="card-body py-3">
                  <div class="d-flex align-items-center flex-wrap mb-2">
                    <span class="font-weight-bold mr-2" style="font-size: 0.82rem;">
                      <i class="fe fe-cpu mr-1 text-warning"></i> AI Recommendation
                    </span>
                    <span :class="{
                        'badge badge-soft-success mr-1': hivaReport.ai_recommendation.overall_recommendation === 'PASS',
                        'badge badge-soft-danger mr-1':  hivaReport.ai_recommendation.overall_recommendation === 'REJECT',
                        'badge badge-soft-warning mr-1': hivaReport.ai_recommendation.overall_recommendation === 'QUERY',
                      }" style="font-size: 0.72rem;">
                      {{ hivaReport.ai_recommendation.overall_recommendation }}
                    </span>
                    <span class="badge badge-soft-secondary" style="font-size: 0.72rem;">
                      {{ hivaReport.ai_recommendation.confidence }}
                    </span>
                  </div>
                  <div class="mb-2" style="font-size: 0.82rem; line-height: 1.6;"
                    v-html="parseMarkdown(hivaReport.ai_recommendation.narrative)"></div>
                  <div
                    v-if="hivaReport.ai_recommendation.key_concerns && hivaReport.ai_recommendation.key_concerns.length">
                    <p class="mb-1 font-weight-bold text-danger" style="font-size: 0.78rem;">Key Concerns:</p>
                    <ul class="mb-0 pl-3" style="font-size: 0.78rem;">
                      <li v-for="(concern, i) in hivaReport.ai_recommendation.key_concerns" :key="i"
                        class="text-danger">
                        {{ concern }}
                      </li>
                    </ul>
                  </div>

                </div>
              </div>

              <!-- Per-item results -->
              <div class="card mb-3" v-if="vetResults && !vetResults.external_api_raw">
                <div class="card-header py-2">
                  <span class="font-weight-bold" style="font-size: 0.85rem;">
                    <i class="fe fe-list text-info mr-1"></i> Item Results
                  </span>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm mb-0">
                    <thead class="bg-light">
                      <tr>
                        <th style="font-size: 0.78rem;">Item</th>
                        <th class="text-right" style="font-size: 0.78rem;">Submitted</th>
                        <th class="text-right" style="font-size: 0.78rem;">Suggested</th>
                        <th class="text-center" style="font-size: 0.78rem;">Confidence</th>
                        <th class="text-center" style="font-size: 0.78rem;">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in vetResults.results" :key="item.claims_service_id">
                        <td style="font-size: 0.82rem;">
                          <span :class="item.is_drug ? 'text-info' : ''">{{ item.item_name }}</span>
                          <span v-if="item.flags.length" class="d-block text-danger" style="font-size: 0.72rem;">
                            <i class="fe fe-alert-circle"></i> {{ item.flags.join('; ') }}
                          </span>
                        </td>
                        <td class="text-right" style="font-size: 0.82rem;">&#8358;{{ item.submitted_cost | numeral(0, 0)
                          }}</td>
                        <td class="text-right" style="font-size: 0.82rem;">
                          <span v-if="item.suggested_verified_price !== null">&#8358;{{ item.suggested_verified_price |
                            numeral(0, 0) }}</span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td class="text-center" style="font-size: 0.78rem;">
                          <span v-if="item.historical_benchmark.confidence !== 'skipped'" :class="{
                              'badge badge-soft-success': item.historical_benchmark.confidence === 'high',
                              'badge badge-soft-warning': item.historical_benchmark.confidence === 'medium',
                              'badge badge-soft-danger': item.historical_benchmark.confidence === 'low' || item.historical_benchmark.confidence === 'none',
                            }">
                            {{ item.historical_benchmark.confidence }}
                          </span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td class="text-center">
                          <span
                            :class="item.action === 'auto_approve' ? 'badge badge-soft-success' : 'badge badge-soft-danger'"
                            style="font-size: 0.72rem;">
                            {{ item.action === 'auto_approve' ? 'Auto-Approve' : 'Flag' }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>


              <!-- External Vetting Report (Layer C) -->
              <div v-if="hivaReport" class="mt-4">
                <div class="d-flex align-items-center mb-3">
                  <span class="font-weight-bold mr-2" style="font-size: 0.85rem;">
                    <i class="fe fe-shield mr-1 text-info"></i> AI Vetting Report
                  </span>
                  <span :class="{
                      'badge badge-soft-success': hivaReport.vetting_status === 'PASS',
                      'badge badge-soft-danger':  hivaReport.vetting_status === 'REJECT',
                      'badge badge-soft-warning': hivaReport.vetting_status === 'QUERY',
                    }" style="font-size: 0.78rem;">
                    {{ hivaReport.vetting_status }}
                  </span>
                  <small class="text-muted ml-auto" v-if="hivaView">Saved result · {{ hivaView.created_at }}</small>
                  <small class="text-muted ml-auto" v-else-if="vetResults && vetResults.hiva">
                    {{ vetResults.hiva.reused ? 'Saved result' : 'New result' }} · {{ vetResults.hiva.created_at }}
                  </small>
                </div>
                <!-- <div class="card mb-3" style="border-left: 4px solid #f6c23e;">
                  <div class="card-body py-3">
                    <div class="d-flex align-items-center flex-wrap mb-2">
                      <span class="font-weight-bold mr-2" style="font-size: 0.82rem;">AI Recommendation</span>
                      <span :class="{
                          'badge badge-soft-success mr-1': hivaReport.ai_recommendation.overall_recommendation === 'PASS',
                          'badge badge-soft-danger mr-1':  hivaReport.ai_recommendation.overall_recommendation === 'REJECT',
                          'badge badge-soft-warning mr-1': hivaReport.ai_recommendation.overall_recommendation === 'QUERY',
                        }" style="font-size: 0.72rem;">
                        {{ hivaReport.ai_recommendation.overall_recommendation }}
                      </span>
                      <span class="badge badge-soft-secondary" style="font-size: 0.72rem;">
                        {{ hivaReport.ai_recommendation.confidence }}
                      </span>
                    </div>
                    <div class="mb-2" style="font-size: 0.82rem; line-height: 1.6;"
                      v-html="parseMarkdown(hivaReport.ai_recommendation.narrative)"></div>
                    <div v-if="hivaReport.ai_recommendation.key_concerns && hivaReport.ai_recommendation.key_concerns.length">
                      <p class="mb-1 text-danger font-weight-bold" style="font-size: 0.78rem;">Key Concerns:</p>
                      <ul class="mb-0 pl-3" style="font-size: 0.78rem;">
                        <li v-for="(concern, i) in hivaReport.ai_recommendation.key_concerns" :key="i" class="text-danger">
                          {{ concern }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div> -->


                <div class="card mb-3" v-if="hivaReport.claim_summary">
                  <div class="card-body py-3">
                    <div class="row text-center">
                      <div class="col-4">
                        <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Total Submitted
                        </p>
                        <h5 class="mb-0">&#8358;{{ hivaReport.claim_summary.total_submitted_amount |
                          numeral(0, 0) }}</h5>
                      </div>
                      <div class="col-4">
                        <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Recommended</p>
                        <h5 class="mb-0 text-success">&#8358;{{
                          hivaReport.claim_summary.total_recommended_amount | numeral(0, 0) }}</h5>
                      </div>
                      <div class="col-4">
                        <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Overcharge</p>
                        <h5 class="mb-0 text-danger">
                          &#8358;{{ (hivaReport.claim_summary.total_submitted_amount -
                          hivaReport.claim_summary.total_recommended_amount) | numeral(0, 0) }}
                        </h5>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="card mb-3">
                  <div class="card-header py-2">
                    <span class="font-weight-bold" style="font-size: 0.82rem;">
                      <i class="fe fe-list text-info mr-1"></i> Checks Breakdown
                    </span>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead class="bg-light">
                        <tr>
                          <th style="font-size: 0.75rem;">Check</th>
                          <th style="font-size: 0.75rem;">Item / Reason</th>
                          <th class="text-center" style="font-size: 0.75rem;">Status</th>
                          <th class="text-right" style="font-size: 0.75rem;">Submitted</th>
                          <th class="text-right" style="font-size: 0.75rem;">Approved</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(chk, i) in hivaReport.checks" :key="i">
                          <td style="font-size: 0.75rem; white-space: nowrap; text-transform: capitalize;">
                            {{ (chk.check_name || '').replace(/_/g, ' ') }}
                          </td>
                          <td style="font-size: 0.75rem;">
                            <span v-if="hivaLineName(chk)" class="font-weight-bold d-block">{{ hivaLineName(chk) }}</span>
                            <span class="text-muted" style="font-size: 0.7rem;">{{ chk.reason }}</span>
                          </td>
                          <td class="text-center">
                            <span :class="{
                                'badge badge-soft-success': chk.status === 'PASS',
                                'badge badge-soft-danger':  chk.status === 'FAIL',
                                'badge badge-soft-warning': chk.status === 'QUERY',
                              }" style="font-size: 0.7rem;">
                              {{ chk.status }}
                            </span>
                          </td>
                          <td class="text-right" style="font-size: 0.78rem;">
                            <span v-if="chk.submitted_value !== null && chk.submitted_value !== undefined">
                              &#8358;{{ chk.submitted_value | numeral(0, 0) }}
                            </span>
                            <span v-else class="text-muted">—</span>
                          </td>
                          <td class="text-right" style="font-size: 0.78rem;">
                            <span v-if="chk.recommended_value !== null && chk.recommended_value !== undefined"
                              :class="chk.status === 'FAIL' ? 'text-danger font-weight-bold' : 'text-success'">
                              &#8358;{{ chk.recommended_value | numeral(0, 0) }}
                            </span>
                            <span v-else class="text-muted">—</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-if="(hivaView || vetResults.hiva) && ((hivaView || vetResults.hiva).remark_codes || []).length" class="mb-2" style="font-size: 0.75rem;">
                  <span class="text-muted mr-1">Remark codes:</span>
                  <span v-for="code in (hivaView || vetResults.hiva).remark_codes" :key="code" class="badge badge-soft-secondary mr-1">{{ code }}</span>
                </div>
                <p class="text-muted text-right mb-0" style="font-size: 0.7rem;">
                  {{ hivaReport.ai_recommendation ? hivaReport.ai_recommendation.generated_by : 'HIVA TRACE' }}
                  <span v-if="hivaReport.metadata"> · {{
                    hivaReport.metadata.duration_ms }}ms</span>
                </p>
              </div>


              <button class="btn btn-success btn-block" @click="applyAutoVet" v-if="vetResults"
                :disabled="vetApplying || vetResults.summary.items_auto_approved === 0">
                <span v-if="vetApplying"><span class="spinner-border spinner-border-sm mr-1"></span> Applying…</span>
                <span v-else>
                  <i class="fe fe-check-circle mr-1"></i>
                  Apply {{ vetResults.summary.items_auto_approved }} Auto-Approved Price(s)
                </span>
              </button>
              <p class="text-muted text-center mt-2" style="font-size: 0.78rem;" v-if="vetResults">
                Only auto-approved items will be written. Flagged items require manual review.
              </p>




            </div>
          </div>
          <div class="modal-footer" style="border-top: 2px solid #e3eaf0;">
            <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Claim Summary Modal ── -->
    <div class="modal fade modal-slide-right" id="claimSummaryModal" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog scrollmodal" role="document" style="width: 520px; max-width: 100%;">
        <div class="modal-content">
          <div class="modal-header bg-soft-info" style="border-bottom: 2px solid #e3eaf0;">
            <div class="d-flex align-items-center">
              <div class="avatar avatar-sm mr-3">
                <div class="avatar-title rounded-circle bg-info text-white"><i class="fe fe-bar-chart-2"></i></div>
              </div>
              <h5 class="modal-title mb-0">Claim Summary Review</h5>
            </div>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="summaryLoading" class="text-center py-5">
              <div class="spinner-border text-info" role="status"></div>
              <p class="mt-2 text-muted">Analysing claim…</p>
            </div>
            <div v-else-if="claimSummary">
              <div v-if="claimSummary.duplicate_check.is_potential_duplicate"
                class="alert alert-danger d-flex align-items-start mb-3" role="alert">
                <i class="fe fe-alert-triangle mr-2 mt-1"></i>
                <div>
                  <strong>Potential Duplicate Detected</strong>
                  <ul class="mb-0 mt-1 pl-3">
                    <li v-for="(reason, i) in claimSummary.duplicate_check.reasons" :key="i">{{ reason }}</li>
                  </ul>
                </div>
              </div>
              <div class="card mb-3" style="border-left: 4px solid #5b8dee;">
                <div class="card-body py-3">
                  <p class="mb-1"
                    style="font-size: 0.78rem; text-transform: uppercase; letter-spacing: .05em; color: #888;">
                    <i class="fe fe-file-text mr-1"></i> Summary
                  </p>
                  <div style="line-height: 1.7;" v-html="parseMarkdown(claimSummary.narrative)"></div>
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-6">
                  <div class="card text-center py-3">
                    <p class="mb-1 text-muted" style="font-size: 0.72rem; text-transform: uppercase;">Claims This Month
                    </p>
                    <h4 class="mb-0 text-info">{{ claimSummary.monthly_stats.total_claims_this_month }}</h4>
                    <p class="mb-0 text-muted" style="font-size: 0.78rem;">{{ claimSummary.monthly_stats.month }}</p>
                  </div>
                </div>
                <div class="col-6">
                  <div class="card text-center py-3">
                    <p class="mb-1 text-muted" style="font-size: 0.72rem; text-transform: uppercase;">Monthly Total</p>
                    <h4 class="mb-0 text-info">&#8358;{{ claimSummary.monthly_stats.total_amount_this_month | numeral(0,
                      0) }}</h4>
                    <p class="mb-0 text-muted" style="font-size: 0.78rem;">all claims combined</p>
                  </div>
                </div>
              </div>
              <div class="card mb-3" v-if="claimSummary.diagnosis_breakdown.length">
                <div class="card-header py-2">
                  <span class="font-weight-bold" style="font-size: 0.85rem;">
                    <i class="fe fe-activity text-info mr-1"></i> Diagnosis Breakdown (Past Month)
                  </span>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm mb-0">
                    <thead class="bg-light">
                      <tr>
                        <th style="font-size: 0.78rem;">Diagnosis</th>
                        <th class="text-center" style="font-size: 0.78rem;">Claims</th>
                        <th class="text-right" style="font-size: 0.78rem;">Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="d in claimSummary.diagnosis_breakdown" :key="d.diagnosis_id">
                        <td style="font-size: 0.82rem;">{{ d.diagnosis_name }}</td>
                        <td class="text-center" style="font-size: 0.82rem;">{{ d.count }}</td>
                        <td class="text-right" style="font-size: 0.82rem;">&#8358;{{ d.total_cost | numeral(0, 0) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-if="claimSummary.duplicate_check.same_diagnosis_this_month.length" class="card mb-3">
                <div class="card-header py-2">
                  <span class="font-weight-bold text-danger" style="font-size: 0.85rem;">
                    <i class="fe fe-copy mr-1"></i> Same Diagnosis This Month
                  </span>
                </div>
                <div class="list-group list-group-flush">
                  <div class="list-group-item px-3 py-2"
                    v-for="c in claimSummary.duplicate_check.same_diagnosis_this_month" :key="c.claim_id">
                    <a :href="`/claim/${c.claim_id}`" class="font-weight-bold text-info" target="_blank">{{
                      c.claim_unique_id }}</a>
                    <span class="mx-1 text-muted">·</span>
                    <span style="font-size: 0.82rem;">{{ c.provider }}</span>
                    <span class="mx-1 text-muted">·</span>
                    <span style="font-size: 0.82rem;">{{ c.date_submitted }}</span>
                    <span :class="`ml-2 badge badge-soft-${c.claim_status === 'approved' ? 'success' : 'warning'}`"
                      style="font-size: 0.72rem;">{{ c.claim_status }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted py-5">
              <i class="fe fe-bar-chart-2" style="font-size: 2rem;"></i>
              <p class="mt-2">Click "Summary Review" to analyse this claim.</p>
            </div>
          </div>
          <div class="modal-footer" style="border-top: 2px solid #e3eaf0;">
            <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import FraudSignals from "@/views/reports/components/FraudSignals.vue";
  import ActivityLogPanel from "@/views/reports/components/ActivityLogPanel.vue";
  import ServiceUsage from "./components/ServiceUsage.vue";
  import ClaimHistoryPanel from "./components/ClaimHistoryPanel.vue";
  import Footer from "@/views/Footer.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";
  import singleClaimMixin from "./mixins/singleClaimMixin";
import claimDraftMixin from "./mixins/claimDraftMixin";
  import claimReviewMixin from "./mixins/claimReviewMixin";
  import { patientLink } from "@/utils/patientLink";

  export default {
    components: { FraudSignals, ActivityLogPanel, ClaimHistoryPanel, ServiceUsage, Navbar, Footer, Loading },
    mixins: [singleClaimMixin, claimDraftMixin, claimReviewMixin],
    data() {
      return { activityOpen: false, historyOpen: false };
    },
    methods: { patientLink },
  };
</script>

<style scoped>
.hiva-options {
  background: #f8fafc;
  border: 1px solid #e3eaf0;
  border-radius: 6px;
  padding: 10px 12px;
}
.calc-rows {
  border: 1px solid #f1f5f9;
  border-radius: 6px;
  overflow: hidden;
}
.calc-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 10px;
  border-bottom: 1px solid #f1f5f9;
}
.calc-row:last-child { border-bottom: none; }
.calc-row-label { margin: 0; }
.calc-row .form-control { max-width: 140px; }

/* ── Page Header ── */
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff;
}
.header-icon-wrap {
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(255,255,255,.15);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.page-subtitle { opacity: .75; font-size: .9rem; }
.mt-n3 { margin-top: -16px; }

/* ── Action Toolbar Card ── */
.action-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  margin-top: -20px;
}
.btn-submit {
  background: linear-gradient(135deg, #1e3a5f, #2563eb);
  color: #fff; border: none; border-radius: 8px;
  padding: 8px 18px; font-weight: 600; font-size: .82rem;
}
.btn-submit:hover { opacity: .9; color: #fff; }
.btn-tool {
  background: #f8fafc; border: 1px solid #e2e8f0;
  color: #374151; border-radius: 8px;
  padding: 6px 14px; font-size: .82rem; font-weight: 500;
}
.btn-tool:hover { background: #f1f5f9; color: #1e3a5f; }
.btn-accept-all {
  background: rgba(255,255,255,0.15);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.35);
  border-radius: 7px;
  font-size: .78rem;
  font-weight: 600;
  padding: 4px 12px;
  transition: background 0.15s;
}
.btn-accept-all:hover { background: rgba(255,255,255,0.28); color: #fff; }
.form-label { font-weight: 600; font-size: .85rem; color: #374151; margin-bottom: 6px; }

/* ── Status badges row ── */
.badge-soft-success { background: #d1fae5; color: #065f46; }
.badge-soft-danger  { background: #fee2e2; color: #991b1b; }
.badge-soft-warning { background: #fef3c7; color: #92400e; }
.badge-soft-info    { background: #dbeafe; color: #1d4ed8; }
.badge-soft-dark    { background: #e5e7eb; color: #1f2937; }
.badge-soft-draft { background: #f1f5f9; color: #475569; border: 1px dashed #94a3b8; }
.badge-soft-forwarded { background: #ede9fe; color: #5b21b6; }
.badge-soft-sent-back { background: #ffedd5; color: #9a3412; }

/* ── Send back / bulk actions ── */
.sent-back-alert { background: #fff7ed; border: 1px solid #fed7aa; color: #7c2d12; border-radius: 10px; }
.btn-outline-sent-back { border: 1px solid #ea580c; color: #c2410c; background: #fff; border-radius: 8px; font-weight: 600; }
.btn-outline-sent-back:hover { background: #fff7ed; color: #9a3412; }
.btn-sent-back { background: #ea580c; color: #fff; border: none; border-radius: 8px; font-weight: 600; }
.btn-sent-back:hover { background: #c2410c; color: #fff; }
.bulk-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 10px 16px; background: #eff6ff; border-bottom: 1px solid #bfdbfe; font-size: .85rem; }
tr.row-selected td { background: #eff6ff; }
.sb-backdrop { position: fixed; inset: 0; z-index: 1070; background: rgba(15, 23, 42, .55); display: flex; align-items: center; justify-content: center; padding: 16px; }
.sb-dialog { background: #fff; border-radius: 12px; width: 100%; max-width: 520px; overflow: hidden; box-shadow: 0 20px 50px rgba(0,0,0,.25); }
.sb-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; background: linear-gradient(135deg, #9a3412 0%, #ea580c 100%); }
.badge-soft-indigo  { background: #e0e7ff; color: #3730a3; }

/* ── Info Cards ── */
.info-card {
  border-radius: 14px;
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,.07);
  overflow: hidden;
}
.info-card-header {
  display: flex; align-items: center;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff;
  font-weight: 700;
  font-size: .88rem;
  letter-spacing: .01em;
}

/* Enrollee photo section */
.info-card .text-center.py-3 {
  background: #f7f9ff;
  border-bottom: 1px solid #e9ecef;
}

/* List group rows */
.list-group-item {
  border-color: #f1f5f9;
}
.list-group-item p.mb-0.text-muted {
  font-size: .72rem;
  text-transform: uppercase;
  letter-spacing: .04em;
}

/* ── Services / Docs Tables ── */
.table thead th {
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: .75rem;
  text-transform: uppercase;
  letter-spacing: .06em;
  border-bottom: 2px solid #e2e8f0;
  padding: 12px 16px;
}
.table tbody td {
  padding: 11px 16px;
  vertical-align: middle;
  border-color: #f1f5f9;
  font-size: .88rem;
}
.table tbody tr:hover { background: #f8fafc; }
.table tfoot td,
.table tbody tr.bg-light { background: #f8fafc !important; font-weight: 700; }

/* ── Modals ── */
.scrollmodal { height: 100%; margin: 0; }
.scrollmodal .modal-content { height: 100%; display: flex; flex-direction: column; }
.scrollmodal .modal-body { overflow-y: auto; flex: 1; }

.modal-header.bg-soft-info {
  background: linear-gradient(135deg, #1e3a5f, #2563eb) !important;
  border-bottom: none !important;
}
.modal-header.bg-soft-info .modal-title { color: #fff; }
.modal-header.bg-soft-info .close { color: #fff; opacity: .8; }
.modal-header.bg-soft-info .close:hover { opacity: 1; }
.modal-header.bg-soft-info .avatar-title { background: rgba(255,255,255,.2) !important; }

/* Comments */
.media {
  background: #f8fafc !important;
  border-left: 3px solid #2563eb !important;
  border-radius: 8px;
}

/* Price calc accent */
.card[style*="border-left: 4px solid #5b8dee"] {
  border-left-color: #2563eb !important;
  border-radius: 10px;
}

/* Extra bottom padding when sticky bar is present */
.pb-queue {
  padding-bottom: 90px !important;
}

/* ── Sticky Queue Navigation Bar ── */
.queue-nav-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1050;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 32px;
  gap: 16px;
}

.btn-queue-nav {
  background: rgba(255, 255, 255, 0.15);
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  border-radius: 10px;
  padding: 10px 28px;
  font-weight: 700;
  font-size: .9rem;
  letter-spacing: .02em;
  transition: background .15s;
  min-width: 110px;
}

.btn-queue-nav:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.28);
  color: #fff;
}

.btn-queue-nav:disabled {
  opacity: .3;
  cursor: not-allowed;
  color: #fff;
}

.queue-nav-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.queue-nav-count {
  font-size: 1.15rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}

.queue-nav-count .text-muted {
  color: rgba(255, 255, 255, .55) !important;
  font-weight: 400;
}

.queue-nav-label {
  font-size: .72rem;
  color: rgba(255, 255, 255, .65);
  letter-spacing: .06em;
  text-transform: uppercase;
}

/* ── Horizontal Request Timeline ── */
.h-timeline {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  row-gap: 16px;
}

.h-timeline-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 130px;
  flex: 0 0 auto;
}

.h-timeline-step--muted {
  opacity: .55;
}

.h-timeline-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  flex-shrink: 0;
  margin-bottom: 8px;
}

.h-timeline-icon--muted {
  background: #e2e8f0;
  color: #94a3b8;
}

.h-timeline-icon--indigo {
  background: #6366f1;
}

.h-timeline-connector {
  flex: 1 1 auto;
  height: 2px;
  min-width: 24px;
  background: #e2e8f0;
  margin-top: 18px;
}

.h-timeline-connector--done {
  background: #10b981;
}

.h-timeline-label strong {
  display: block;
  font-size: .85rem;
  color: #374151;
  margin-bottom: 2px;
}

.h-timeline-label span,
.h-timeline-label a {
  display: block;
  font-size: .75rem;
  color: #64748b;
  line-height: 1.4;
}
</style>